type ButtonActionLike = {
    id?: string;
    actionCode: string;
    options?: unknown;
};

export type SignActionPreparationInfo = {
    buttonActionId: string;
    targetServiceProcessFieldId: string | null;
    needsSignPreparation: boolean;
};

function parseOptions(value: unknown): Record<string, unknown> | null {
    try {
        const options =
            typeof value === 'string' ? (JSON.parse(value) as unknown) : value;
        if (!options || typeof options !== 'object') return null;
        return options as Record<string, unknown>;
    } catch {
        return null;
    }
}

/** First Sign action on the button; used for PrepareDocumentSign. */
export function resolveSignActionPreparation(
    buttonActions?: ButtonActionLike[],
): SignActionPreparationInfo | null {
    const signAction = buttonActions?.find(
        (action) => action.actionCode === 'Sign',
    );
    if (!signAction?.id) return null;

    const options = parseOptions(signAction.options);
    const target =
        typeof options?.targetServiceProcessFieldId === 'string'
            ? options.targetServiceProcessFieldId
            : null;

    return {
        buttonActionId: signAction.id,
        targetServiceProcessFieldId: target,
        needsSignPreparation: Boolean(target),
    };
}

export type CancelDocumentSignReasonKind =
    | 'sheetClosed'
    | 'userCancelled'
    | 'clientError';

export function getCancelDocumentSignReason(
    kind: CancelDocumentSignReasonKind,
): string {
    switch (kind) {
        case 'sheetClosed':
            return 'Пользователь закрыл окно подписания';
        case 'userCancelled':
            return 'Пользователь отменил подписание';
        case 'clientError':
            return 'Ошибка подписания на клиенте';
    }
}

export const VERIFY_SIGN_SESSION_KEY = 'office_web_verify_sign';

export function isVerifySignRevealed(
    storage: Pick<Storage, 'getItem'> = sessionStorage,
): boolean {
    return storage.getItem(VERIFY_SIGN_SESSION_KEY) === '1';
}

export function setVerifySignRevealed(
    revealed: boolean,
    storage: Pick<Storage, 'setItem' | 'removeItem'> = sessionStorage,
): void {
    if (revealed) {
        storage.setItem(VERIFY_SIGN_SESSION_KEY, '1');
    } else {
        storage.removeItem(VERIFY_SIGN_SESSION_KEY);
    }
}

/** Returns true when the 5th consecutive click reveals verify UI. */
export function registerVerifySignGestureClick(
    clickCount: number,
    storage: Pick<Storage, 'setItem' | 'removeItem'> = sessionStorage,
): { nextCount: number; revealed: boolean } {
    const nextCount = clickCount + 1;
    if (nextCount >= 5) {
        setVerifySignRevealed(true, storage);
        return { nextCount: 0, revealed: true };
    }
    return { nextCount, revealed: false };
}

export type ServiceSignPreparationResult = {
    preparationId: string;
    fileBase64: string;
    fileName?: string;
    contentType?: string;
    capturedAt?: string;
    expiresAt?: string;
};

export type SignPreparationFieldValues = Record<string, unknown>;

export type PrepareDocumentSignCommand = {
    serviceId: string;
    buttonId: string;
    buttonActionId: string;
    fields: SignPreparationFieldValues;
};

export const signPreparationFieldsKey = 'signPreparationFields';

export type SignFromFormDecision =
    | { type: 'invalid' }
    | { type: 'prepare'; fields: SignPreparationFieldValues }
    | { type: 'existingFile' };

/**
 * Sign preparation stores the validated form. A sign of an already stored
 * file does not read the form, dirty or not.
 */
export function decideSignFromForm(input: {
    needsSignPreparation: boolean;
    validatedFields: SignPreparationFieldValues | undefined;
}): SignFromFormDecision {
    if (input.needsSignPreparation) {
        if (!input.validatedFields) return { type: 'invalid' };
        return { type: 'prepare', fields: input.validatedFields };
    }

    return { type: 'existingFile' };
}

export function readSignPreparationFields(
    extraValues: Record<string, unknown> | null | undefined,
): SignPreparationFieldValues | undefined {
    const value = extraValues?.[signPreparationFieldsKey];
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }
    return value as SignPreparationFieldValues;
}

/** Prepared values win over edits made while prepare was in flight. */
export function formValuesAfterPreparationSaved(
    currentValues: Record<string, unknown>,
    preparedFields: SignPreparationFieldValues,
): Record<string, unknown> {
    return { ...currentValues, ...preparedFields };
}

export type CancelDocumentSignCommand = {
    preparationId: string;
    reason?: string;
};

export type MobileSignSessionCreateCommand = {
    intent: 'ServiceDocumentSign' | 'ApprovalDecisionSign';
    serviceId: string;
    buttonId: string;
    buttonActionId?: string;
    isMobile: boolean;
    signPreparationId?: string | null;
    fields?: Record<string, unknown>;
    createdWithQr?: boolean;
};

export function needsDocumentSignPreparation(data: {
    needsSignPreparation?: boolean;
    signButtonActionId?: string | null;
    serviceId?: string;
    buttonId?: string;
}): boolean {
    return Boolean(
        data.needsSignPreparation &&
        data.signButtonActionId &&
        data.serviceId &&
        data.buttonId,
    );
}

/** Desktop QR reuses the parent Sign preparation. It does not prepare again. */
export function desktopQrSessionCreate(input: {
    intent: MobileSignSessionCreateCommand['intent'];
    serviceId: string;
    buttonId: string;
    signButtonActionId?: string | null;
    signPreparationId?: string | null;
}): MobileSignSessionCreateCommand {
    return buildMobileSignSessionCreateCommand({
        intent: input.intent,
        serviceId: input.serviceId,
        buttonId: input.buttonId,
        signButtonActionId: input.signButtonActionId,
        isMobile: false,
        createdWithQr: true,
        ...(input.signPreparationId
            ? { signPreparationId: input.signPreparationId }
            : {}),
    });
}

/** One Sign preparation is created by PrepareDocumentSign. The session only names that Sign action. */
export function buildMobileSignSessionCreateCommand(input: {
    intent: MobileSignSessionCreateCommand['intent'];
    serviceId: string;
    buttonId: string;
    signButtonActionId?: string | null;
    isMobile: boolean;
    signPreparationId?: string | null;
    fields?: Record<string, unknown>;
    createdWithQr?: boolean;
}): MobileSignSessionCreateCommand {
    return {
        intent: input.intent,
        serviceId: input.serviceId,
        buttonId: input.buttonId,
        ...(input.signButtonActionId
            ? { buttonActionId: input.signButtonActionId }
            : {}),
        isMobile: input.isMobile,
        signPreparationId: input.signPreparationId,
        fields: input.fields,
        createdWithQr: input.createdWithQr,
    };
}

export function attachSignPreparationId<T extends object>(
    payload: T,
    preparationId: string | null | undefined,
): T & { signPreparationId?: string } {
    if (!preparationId) return payload;
    return { ...payload, signPreparationId: preparationId };
}

/** Cancel only while a Sign preparation is still open. Success leaves it unused by Cancel. */
export function shouldCancelSignPreparation(input: {
    preparationId: string | null | undefined;
    signedSuccessfully: boolean;
}): boolean {
    return Boolean(input.preparationId) && !input.signedSuccessfully;
}

export type SignAttemptFailureKind =
    | 'ncalayerError'
    | 'ncalayerConnectionClosed'
    | 'userCancelled'
    | 'other';

export function classifySignAttemptFailure(
    err: unknown,
): SignAttemptFailureKind {
    if (err && typeof err === 'object' && 'message' in err) {
        const message = String(err.message);
        if (message === 'NCALAYER_ERROR') return 'ncalayerError';
        if (message === 'NCALAYER_CONNECTION_CLOSED') {
            return 'ncalayerConnectionClosed';
        }
        if (message === 'cancelled by user') return 'userCancelled';
    }
    return 'other';
}

/**
 * Signer UX errors are toasted in SignFlow. Execute (and similar) already
 * toast via the mutation handler — do not stack a second generic error.
 */
export function shouldNotifySignAttemptFailureInSignFlow(
    kind: SignAttemptFailureKind,
): boolean {
    return kind !== 'other';
}
